<template>
  <div
    id="section-gc"
    class="content-section content-section--color-light theme-light paywall"
    :class="{ 'paywall--gated': !isLead }"
  >
    <div class="content-section__inner">
      <GatedContent
        v-if="showGatedContent"
        id="paywall"
        :headline="headline"
        :intro-text="isLead && leadSubmitted ? undefined : introText"
        :col01-headline="
          rendered && showBaslerId
            ? ($textDictionary['paywall.col01.headline'] ??
              'Continue with My Basler')
            : undefined
        "
        :col02-headline="
          rendered && showForm && !isLead
            ? ($textDictionary['paywall.col02.headline'] ??
              'Continue without an account')
            : undefined
        "
      >
        <template v-if="showBaslerId" #col01Content>
          <div v-if="!rendered" class="paywall__skeleton">
            <LoadingSkeleton class="paywall__skeleton-line" />
            <LoadingSkeleton class="paywall__skeleton-block" />
            <LoadingSkeleton class="paywall__skeleton-block" />
          </div>
          <template v-else>
            <LibRichTextRenderer
              v-if="baslerIdDescription"
              :nodes-renderer="nodesRenderer"
              :rich-text="baslerIdDescription"
            />
            <div class="gated-content__login-box paywall__login-box">
              <LinkElement
                data-testid="paywall-login"
                type="button"
                style-type="attraction"
                size="large"
                :link="
                  {
                    uri: loginUrl,
                    target: '_self',
                    rel: 'nofollow'
                  } satisfies HyperlinkInterface
                "
              >
                {{
                  $textDictionary['paywall.loginButton.label'] ??
                  'Sign in to My Basler'
                }}
              </LinkElement>
              <p>
                {{
                  $textDictionary['paywall.noAccount.label'] ??
                  'New to My Basler?'
                }}
                <LinkElement
                  data-testid="paywall-register"
                  css-class="gated-content__link"
                  :link="
                    {
                      uri: registerUrl,
                      target: '_self',
                      rel: 'nofollow'
                    } satisfies HyperlinkInterface
                  "
                >
                  {{
                    $textDictionary['paywall.register.label'] ??
                    'Register for free'
                  }}
                </LinkElement>
              </p>
            </div>
          </template>
        </template>
        <template v-if="showLeadColumn" #col02Content>
          <div v-if="!rendered" class="paywall__skeleton">
            <LoadingSkeleton class="paywall__skeleton-line" />
            <LoadingSkeleton class="paywall__skeleton-block" />
            <LoadingSkeleton class="paywall__skeleton-block" />
          </div>
          <div
            v-else-if="isLead && leadSubmitted"
            class="paywall__success"
            data-testid="paywall-success"
          >
            <LibRichTextRenderer
              v-if="formSuccessMessage"
              :rich-text="formSuccessMessage"
            />
          </div>
          <div
            v-else-if="showLoggedInLeadButton"
            class="gated-content__form-cta"
          >
            <Button
              data-testid="paywall-download"
              type="button"
              style-type="attraction-secondary"
              size="medium"
              icon-type="no-icon"
              :active-state="submitButtonState"
              :states="submitButtonStates"
              @click="onLoggedInLeadClick"
              @update:active-state="handleSubmitButtonStateUpdate"
            >
              {{ submitLabel }}
            </Button>
          </div>
          <form
            v-else
            data-testid="paywall-form"
            class="gated-content__form"
            @submit.prevent="onFormSubmit"
          >
            <div v-if="!isLead" class="form-item">
              <PrimeVueFloatLabel variant="on">
                <PrimeVueInputText
                  id="gc-firstname"
                  v-model="formData.firstname"
                  name="gc-firstname"
                  autocomplete="given-name"
                  required
                />
                <label for="gc-firstname" class="p-label">{{
                  $textDictionary['floatingContact.form.firstName'] ??
                  'First name'
                }}</label>
              </PrimeVueFloatLabel>
            </div>
            <div v-if="!isLead" class="form-item">
              <PrimeVueFloatLabel variant="on">
                <PrimeVueInputText
                  id="gc-lastname"
                  v-model="formData.lastname"
                  name="gc-lastname"
                  autocomplete="family-name"
                  required
                />
                <label for="gc-lastname" class="p-label">{{
                  $textDictionary['floatingContact.form.lastName'] ??
                  'Last name'
                }}</label>
              </PrimeVueFloatLabel>
            </div>
            <div v-if="!isLead" class="form-item">
              <PrimeVueFloatLabel variant="on">
                <PrimeVueInputText
                  id="gc-company"
                  v-model="formData.company"
                  name="gc-company"
                  autocomplete="organization"
                  required
                />
                <label for="gc-company" class="p-label">{{
                  $textDictionary['floatingContact.form.company'] ?? 'Company'
                }}</label>
              </PrimeVueFloatLabel>
            </div>
            <div class="form-item">
              <PrimeVueFloatLabel variant="on">
                <PrimeVueInputText
                  id="gc-email"
                  v-model="formData.email"
                  name="gc-email"
                  type="email"
                  autocomplete="email"
                  required
                />
                <label for="gc-email" class="p-label">{{
                  $textDictionary['floatingContact.form.email'] ??
                  'Work Email address'
                }}</label>
              </PrimeVueFloatLabel>
            </div>
            <div class="visually-hidden" aria-hidden="true">
              <label for="gc-website">Website</label>
              <input
                id="gc-website"
                v-model="formData.website"
                type="text"
                name="website"
                tabindex="-1"
                v-bind="PASSWORD_MANAGER_IGNORE_ATTRS"
              />
            </div>
            <div class="form-check">
              <PrimeVueCheckbox
                v-model="formData.agreeTerms"
                input-id="gc-agree-terms"
                name="gc-agree-terms"
                :binary="true"
              />
              <label for="gc-agree-terms" class="p-label">
                <DictionaryRichText
                  dictionary-key="paywall.form.agreeTerms.label"
                  fallback="I agree to the merging of pseudonymized usage profiles with my personal data. I can revoke my consent at any time. For further information, please see our privacy policy."
                />
              </label>
            </div>
            <div class="gated-content__form-cta">
              <Button
                data-testid="paywall-form-submit"
                type="submit"
                style-type="attraction-secondary"
                size="medium"
                icon-type="no-icon"
                :disabled="!canSubmitForm"
                :active-state="submitButtonState"
                :states="submitButtonStates"
                @update:active-state="handleSubmitButtonStateUpdate"
              >
                {{ submitLabel }}
              </Button>
            </div>
          </form>
          <div v-if="error" class="paywall__error">
            <AlertBox :style="'danger'">{{ error }}</AlertBox>
            <Button
              v-if="!formChallenge"
              data-testid="paywall-form-challenge-retry"
              type="button"
              style-type="attraction-secondary"
              size="medium"
              icon-type="no-icon"
              class="paywall__challenge-retry"
              @click="fetchFormChallenge"
            >
              {{
                $textDictionary['paywall.form.challenge.retry.label'] ??
                'Try again'
              }}
            </Button>
          </div>
        </template>

        <template v-if="!isLead" #content>
          <div class="paywall-content paywall__preview">
            <LibGroupedSections
              v-if="!isValidBot && gatedContent.length"
              :entries="gatedContent"
              :as-section="true"
            />
          </div>
        </template>
      </GatedContent>
    </div>

    <div v-if="isValidBot && gatedContent.length" class="paywall-content">
      <MappedComponents :items="gatedContent" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue';
import {
  GatedContent,
  Icon,
  Button,
  LinkElement,
  LoadingSkeleton,
  AlertBox
} from '@hypercodestudio/basler-components';
import type {
  BtnStateType,
  HyperlinkInterface
} from '@hypercodestudio/basler-components';

import { buildAuthLoginUri } from '~/utils/buildAuthLoginUri';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { type Block, BLOCKS } from '@contentful/rich-text-types';
import {
  markPaywallFormhandlerCapture,
  submitLoggedInPaywallUnlock,
  type PaywallFormhandlerCaptureIdentity
} from '~/utils/paywall/paywallFormhandlerCapture';
import { isLeadPaywall } from '~/utils/paywall/isLeadPaywall';
import { resolvePaywallSubmitLabel } from '~/utils/paywall/resolvePaywallSubmitLabel';
import { FORM_CHALLENGE_MIN_FILL_MS } from '~/utils/form/formChallengeConstants';
import { PASSWORD_MANAGER_IGNORE_ATTRS } from '~/utils/form/passwordManagerIgnoreAttrs';
import type { IPaywallExtended } from '~~/lib/Contentful/model';

import type { NodesRenderer } from 'contentful-rich-text-vue-renderer';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { getLocaleCountryLabel } from '~/stores/geoinformationStore/helper/getLocaleCountryLabel';
import { mapShopStoreToSalesRegion } from '~/utils/shop/mapShopStoreToSalesRegion';
import { extractIso31662Subdivision } from '~/utils/geolocation/extractIso31662Subdivision';
import { CONTENT_PAGE_TITLE, TOC_INFO } from '~~/lib/constants';
import type { TOCInfo } from '~/types/TocType';
import { groupSections } from '~/utils/groupSections';
import { resolveContentSectionId } from '~/utils/resolveContentSectionId';
import waitForElement from '~/utils/waitForElement';

interface Props {
  item: IPaywallExtended;
}

const props = defineProps<Props>();

const { isValidBot, isPaywallVisible, markGatedContentUnlocked } = usePaywall();
const { $dictionary, $textDictionary, $analytics, $activeLocale, $shopStore } =
  useNuxtApp();
const route = useRoute();
const { loggedIn, user } = useShopAuth();
const geoinformationStore = useGeoinformationStore();
const requestURL = useRequestURL();
const contentPageTitle = inject<ComputedRef<string>>(
  CONTENT_PAGE_TITLE,
  computed(() => '')
);

// Geo detection as primary source, locale code as fallback (e.g. on localhost)
// Country name intentionally in German, same as auto-submit in ContentPage (Pardot)
const resolvedCountry = computed(() => {
  const geoCode = geoinformationStore.detectedCountry;
  if (geoCode) {
    return getLocaleCountryLabel(geoCode, 'de') ?? geoCode;
  }
  const localeCode = $activeLocale.value.webapp
    ?.split('-')
    .at(-1)
    ?.toUpperCase();
  return getLocaleCountryLabel(localeCode, 'de') ?? localeCode;
});

const salesRegion = computed(() =>
  mapShopStoreToSalesRegion(String($shopStore.value ?? ''))
);

const territory = computed(() =>
  extractIso31662Subdivision(geoinformationStore.detectedRegion)
);

const rendered = ref<boolean>(false);
const error = ref<string | null>(null);
const leadSubmitted = ref(false);
const submitButtonState = ref<BtnStateType | undefined>(undefined);
const logger = useLogger();
/** Signed timing token from GET /api/formhandler/challenge. */
const formChallenge = ref('');
/** Becomes true FORM_CHALLENGE_MIN_FILL_MS after a challenge is fetched. */
const minFillElapsed = ref(false);
let minFillTimer: ReturnType<typeof setTimeout> | undefined;

const handleSubmitButtonStateUpdate = (state: BtnStateType) => {
  submitButtonState.value = state;
};

const nodesRenderer: NodesRenderer = {
  [BLOCKS.UL_LIST]: (node, key, next) => {
    return h(
      'ul',
      { key, class: 'richtext__ul' },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.LIST_ITEM]: (node, key, next) => {
    return h('li', { key, class: 'richtext__li' }, [
      h(Icon, { name: 'checkmark-circle-full', color: '#4EC624' }),
      next?.(node.content as Block[], key, next)
    ]);
  },
  text: (_node, _key, _markRenderer) => '' // necessary by type but will never be used
};

const formData = reactive({
  firstname: '',
  lastname: '',
  company: '',
  email: '',
  website: '',
  agreeTerms: false
});

const isLead = computed(() => isLeadPaywall(props.item));

const showGatedContent = computed(
  () => !isValidBot.value && (isPaywallVisible.value || isLead.value)
);

const showLoggedInLeadButton = computed(
  () =>
    isLead.value &&
    loggedIn.value &&
    !!props.item?.fields.formHandlerUrl &&
    !leadSubmitted.value
);

const showLeadColumn = computed(
  () =>
    showForm.value ||
    showLoggedInLeadButton.value ||
    (isLead.value && leadSubmitted.value)
);

const formSuccessMessage = computed(
  () => props.item?.fields.formSuccessMessage
);

const submitLabel = computed(() =>
  resolvePaywallSubmitLabel({
    buttonText: props.item?.fields.buttonText,
    dictionaryLabel: isLead.value
      ? $textDictionary.value?.['paywall.lead.button.label']
      : $textDictionary.value?.['paywall.form.submit.label'],
    fallback: isLead.value ? 'Download' : 'Unlock for free and continue reading'
  })
);

const submitButtonStates = computed(() => ({
  loading: {
    text:
      $textDictionary.value?.['paywall.form.submit.loading.label'] ??
      'Unlocking content…',
    ariaText:
      $textDictionary.value?.['paywall.form.submit.loading.ariaText'] ??
      'Unlocking content',
    resetDelay: -1
  },
  error: {
    text:
      $textDictionary.value?.['paywall.form.submit.error.label'] ??
      'Unlock failed',
    ariaText:
      $textDictionary.value?.['paywall.form.submit.error.ariaText'] ??
      'Content could not be unlocked',
    resetDelay: 1000
  }
}));

const canSubmitForm = computed(
  () =>
    (!isLead.value || formData.email.trim().length > 0) &&
    formData.agreeTerms &&
    submitButtonState.value !== 'loading' &&
    minFillElapsed.value &&
    formChallenge.value.length > 0
);

const leadCaptureIdentity = ():
  PaywallFormhandlerCaptureIdentity | undefined => {
  if (!isLead.value) {
    return undefined;
  }

  if (!loggedIn.value) {
    return { kind: 'guest' };
  }

  const baslerId = user.value?.claims?.oid
    ? String(user.value.claims.oid)
    : undefined;
  if (!baslerId) {
    return undefined;
  }

  return { kind: 'user', baslerId };
};

const getUnlockedSectionId = () => {
  const firstGroup = groupSections(gatedContent.value).at(0);
  if (!firstGroup || firstGroup.type === 'sectionItemEntry') {
    return undefined;
  }

  return (
    resolveContentSectionId(
      firstGroup.section?.fields.id,
      firstGroup.section?.sys.id,
      firstGroup.items[0]?.sys.id
    ) ?? 'content-section'
  );
};

const scrollToUnlockedSection = async (sectionId: string | undefined) => {
  if (!sectionId) {
    return;
  }

  // Let Vue remove the gated preview before waiting for the same section ID
  // to reappear with the refetched full article.
  await nextTick();
  const section = await waitForElement(sectionId);
  section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

async function onFormSubmit() {
  if (!canSubmitForm.value) {
    return;
  }

  error.value = null;
  submitButtonState.value = 'loading';

  try {
    await $fetch('/api/formhandler', {
      method: 'POST',
      body: {
        internal_locale: $activeLocale.value.webapp,
        internal_entryID: props.item?.sys.id,
        ...(isLead.value
          ? {}
          : {
              firstname: formData.firstname,
              lastname: formData.lastname,
              company: formData.company
            }),
        email: formData.email,
        filename: requestURL.href,
        country: resolvedCountry.value,
        hp_website: formData.website,
        form_challenge: formChallenge.value,
        ...(salesRegion.value ? { sales_region: salesRegion.value } : {}),
        ...(territory.value ? { territory: territory.value } : {}),
        download: 'document',
        ...(loggedIn.value && user.value?.claims?.oid
          ? { basler_id: String(user.value.claims.oid) }
          : {})
      }
    });

    $analytics.pushToDataLayer({
      event: 'formSent',
      goalPage: '/downloadform/sent',
      page_location: requestURL.href
    });

    submitButtonState.value = undefined;

    if (props.item?.sys.id) {
      markPaywallFormhandlerCapture(props.item.sys.id, { kind: 'guest' });
    }

    if (isLead.value) {
      leadSubmitted.value = true;
      return;
    }

    // Server Set-Cookie (HttpOnly) already issued. Flip client unlock state →
    // isPaywallVisible false → useContentfulPage force-refetches with cookie.
    const unlockedSectionId = getUnlockedSectionId();
    markGatedContentUnlocked();
    void scrollToUnlockedSection(unlockedSectionId);
  } catch {
    submitButtonState.value = 'error';
    formChallenge.value = '';
    minFillElapsed.value = false;
    if (minFillTimer !== undefined) {
      clearTimeout(minFillTimer);
      minFillTimer = undefined;
    }
    if (isLead.value) {
      void fetchFormChallenge();
      return;
    }
    error.value =
      $textDictionary.value?.['paywall.form.error.message'] ??
      'We could not unlock the content. Please try again.';
  }
}

const onLoggedInLeadClick = async () => {
  const identity = leadCaptureIdentity();
  if (!identity || identity.kind !== 'user' || !props.item?.sys.id) {
    submitButtonState.value = 'error';
    return;
  }

  submitButtonState.value = 'loading';

  try {
    if (!geoinformationStore.initiated) {
      await geoinformationStore.init();
    }
  } catch (err: unknown) {
    logger.warn('[Paywall] geolocation init failed before lead capture', err);
  }

  const companyClaim = user.value?.claims?.Company;

  try {
    const submitted = await submitLoggedInPaywallUnlock({
      locale: String($activeLocale.value.webapp ?? ''),
      entryId: props.item.sys.id,
      firstname:
        typeof user.value?.userInfo?.given_name === 'string'
          ? user.value.userInfo.given_name
          : undefined,
      lastname:
        typeof user.value?.userInfo?.family_name === 'string'
          ? user.value.userInfo.family_name
          : undefined,
      email:
        typeof user.value?.userInfo?.email === 'string'
          ? user.value.userInfo.email
          : undefined,
      company: typeof companyClaim === 'string' ? companyClaim : undefined,
      baslerId: identity.baslerId,
      filename: requestURL.href,
      country: resolvedCountry.value,
      ...(salesRegion.value ? { salesRegion: salesRegion.value } : {}),
      ...(territory.value ? { territory: territory.value } : {})
    });

    if (submitted) {
      $analytics.pushToDataLayer({
        event: 'formSent',
        goalPage: '/downloadform/sent',
        page_location: requestURL.href
      });
    }

    leadSubmitted.value = true;
    submitButtonState.value = undefined;
  } catch (err: unknown) {
    logger.warn('[Paywall] logged-in lead capture failed', err);
    submitButtonState.value = 'error';
  }
};

const gatedContent = computed(() => props.item.extension?.nextEntries ?? []);
const loginUrl = computed(() =>
  buildAuthLoginUri(route.path, route.query, 'login')
);

const registerUrl = computed(() =>
  buildAuthLoginUri(route.path, route.query, 'create')
);

const replaceTitlePlaceholder = (template: string, title: string) =>
  template.replaceAll('{title}', title);

const escapeHtml = (text: string) =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const headline = computed(() => {
  const contentfulTitle = props.item?.fields.title?.trim();
  const template =
    contentfulTitle ||
    $textDictionary.value['paywall.title'] ||
    'Unlock {title}';
  return replaceTitlePlaceholder(template, contentPageTitle.value);
});

const introText = computed(() => {
  const description =
    props.item?.fields.description || $dictionary.value['paywall.description'];
  if (!description) {
    return replaceTitlePlaceholder(
      'Unlock the full technical article “{title}”. Sign in to My Basler or use the form – free of charge and without obligation.',
      escapeHtml(contentPageTitle.value)
    );
  }

  const html = documentToHtmlString(description, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, next) => next(node.content)
    }
  });
  return replaceTitlePlaceholder(html, escapeHtml(contentPageTitle.value));
});

const baslerIdDescription = computed(
  () =>
    props.item?.fields.baslerIdDescription ||
    $dictionary.value['paywall.baslerIdDescription']
);

const showBaslerId = computed(
  () => !isLead.value && props.item?.fields.paywallType !== 'Only Form'
);

const showForm = computed(
  () =>
    !!props.item?.fields.formHandlerUrl &&
    props.item?.fields.paywallType !== 'Only Basler ID' &&
    !(isLead.value && loggedIn.value)
);

async function fetchFormChallenge() {
  minFillElapsed.value = false;
  if (minFillTimer !== undefined) {
    clearTimeout(minFillTimer);
    minFillTimer = undefined;
  }

  error.value = null;

  try {
    const response = await $fetch<{ challenge: string }>(
      '/api/formhandler/challenge'
    );
    formChallenge.value = response.challenge ?? '';
    if (formChallenge.value.length > 0) {
      minFillTimer = setTimeout(() => {
        minFillElapsed.value = true;
      }, FORM_CHALLENGE_MIN_FILL_MS);
    } else {
      error.value =
        $textDictionary.value?.['paywall.form.challenge.error.message'] ??
        'We could not prepare the form. Please try again.';
    }
  } catch {
    formChallenge.value = '';
    error.value =
      $textDictionary.value?.['paywall.form.challenge.error.message'] ??
      'We could not prepare the form. Please try again.';
  }
}

onMounted(() => {
  rendered.value = true;

  if (showForm.value) {
    void fetchFormChallenge();
  }
});

onBeforeUnmount(() => {
  if (minFillTimer !== undefined) {
    clearTimeout(minFillTimer);
  }
});
</script>

<style scoped>
.paywall__success {
  text-align: center;
}

.paywall__success :deep(p) {
  text-align: center;
}

.paywall--gated :deep(.gated-content) {
  overflow-y: hidden;
  padding-bottom: 240px;
}

.paywall :deep(.gated-content__overlay) {
  z-index: 2;
}

.paywall :deep(.richtext__ul) {
  margin-bottom: 1em;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.paywall :deep(.richtext__li) {
  display: flex;
  align-items: center;
  gap: 8px;
}

.paywall__skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.paywall__skeleton-line {
  width: 100%;
  height: 40px;
}

.paywall__skeleton-block {
  width: 100%;
  height: 200px;
}

.paywall__login-box {
  margin-top: 36px;
}

.paywall__error {
  margin-top: 16px;
}

.paywall__challenge-retry {
  margin-top: 12px;
}

.paywall__preview {
  height: 250px;
}
</style>
